<template>
  <!--
    ArtifactList — WHAT: the LIST half of every artifact's list→detail flow. One
    generic table driven entirely by the registry's `columns` + `searchFields`
    (search box, click-to-sort headers, typed cells, +New button, per-row delete).
    Every list-based artifact reuses it; there are no per-type list components.

    WHERE: rendered by MetricStudio.vue for the active (non-fullManager) artifact
    whenever no record is selected. Parent = MetricStudio.vue.

    CONTRACT
      props:
        artifact (Object, req) — the registry entry; supplies columns, searchFields,
          idField, label/singular, accent, and deleteUrl (× shown only if present).
        rows (Array)           — the records to display (parent-loaded).
        loading (Bool)         — show the "Loading…" state.
        error (String)         — show an error message instead of the table.
        creating (Bool)        — disables the +New button mid-create.
      emits:
        open(row)    — a row was clicked (parent opens the detail view).
        create()     — the +New button was clicked.
        delete(row)  — the row's × was clicked (@click.stop; parent confirms).

    NOTES: purely presentational — no data fetching or persistence here. Cells are
    rendered by column flags (check / dot / chip:'color' / badge / date / compute).
    Sort is client-side with empties always sorted last; strings compared
    lowercased, booleans as 0/1. The scroll container is capped at
    max-height: calc(100vh - 220px) with a sticky header so long lists scroll
    inside the card instead of the page. `magnifying-glass` / `plus` icons must be
    registered in src/main.js.
  -->
  <div class="flex flex-col">
    <!-- IN-UI HELP: what this artifact is + how to author it. Shown above the
         list so a first-time user knows what the screen does. -->
    <div
      v-if="artifact.help"
      class="flex items-start gap-2.5 mb-3 px-3.5 py-2.5 rounded-lg border text-xs leading-relaxed"
      :style="{ background: artifact.accent + '0D', borderColor: artifact.accent + '33', color: '#374151' }"
    >
      <font-awesome-icon :icon="['fas', 'circle-info']" class="mt-0.5 flex-shrink-0" :style="{ color: artifact.accent }" />
      <span>{{ artifact.help }}</span>
    </div>

    <!-- list toolbar -->
    <div class="flex items-center gap-3 px-1 pb-3">
      <div class="relative flex-1 max-w-sm">
        <font-awesome-icon :icon="['fas', 'magnifying-glass']" class="absolute left-3 top-1/2 -translate-y-1/2 text-gray-300 text-xs" />
        <input
          v-model="query"
          type="text"
          :placeholder="`Search ${artifact.label.toLowerCase()}…`"
          class="w-full pl-8 pr-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:border-blue-400"
        />
      </div>
      <span class="text-[11px] text-gray-400">{{ filtered.length }} of {{ rows.length }}</span>
      <div class="flex-1"></div>
      <button
        class="inline-flex items-center gap-1.5 text-xs font-semibold text-white rounded-lg px-3 py-2 hover:opacity-90 disabled:opacity-50"
        :style="{ background: artifact.accent }"
        :disabled="creating"
        @click="$emit('create')"
      >
        <font-awesome-icon :icon="['fas', 'plus']" class="text-[10px]" />
        New {{ artifact.singular }}
      </button>
    </div>

    <div v-if="loading" class="text-[11px] text-gray-500 px-1 py-6">Loading…</div>
    <div v-else-if="error" class="text-[11px] text-red-600 px-1 py-6">{{ error }}</div>

    <div v-else class="overflow-auto bg-white border border-gray-200 rounded-xl" style="max-height: calc(100vh - 220px);">
      <table class="min-w-full divide-y divide-gray-200">
        <thead class="bg-gray-50 sticky top-0 z-10">
          <tr>
            <th
              v-for="col in artifact.columns"
              :key="col.key"
              class="px-3 py-2 text-left text-[11px] font-semibold text-gray-600 whitespace-nowrap cursor-pointer select-none hover:text-gray-900"
              :class="col.width"
              @click="toggleSort(col.key)"
            >
              {{ col.label }}
              <span v-if="sortKey === col.key" class="text-gray-400">{{ sortDir === 'asc' ? '▲' : '▼' }}</span>
            </th>
            <th class="px-2 py-2 w-10"></th>
          </tr>
        </thead>
        <tbody class="divide-y divide-gray-100">
          <tr
            v-for="row in filtered"
            :key="row[artifact.idField]"
            class="hover:bg-gray-50 cursor-pointer border-l-2"
            :class="isMuted(row)
              ? 'opacity-50 border-l-transparent'
              : (artifact.rowMuted ? 'border-l-current bg-white' : 'border-l-transparent')"
            :style="!isMuted(row) && artifact.rowMuted ? { borderLeftColor: artifact.accent } : null"
            @click="$emit('open', row)"
          >
            <td
              v-for="col in artifact.columns"
              :key="col.key"
              class="px-3 py-1.5 text-xs align-middle"
              :class="[col.width, col.grow ? 'max-w-xs truncate' : 'whitespace-nowrap', col.mono ? 'font-mono' : '', col.muted ? 'text-gray-400' : 'text-gray-800']"
              :title="col.grow ? cellText(row, col) : null"
            >
              <!-- check -->
              <template v-if="col.check">
                <span v-if="cellValue(row, col)" class="text-emerald-600">✓</span>
                <span v-else class="text-gray-300">—</span>
              </template>
              <!-- active dot -->
              <template v-else-if="col.dot">
                <span :class="cellValue(row, col) ? 'text-emerald-500' : 'text-gray-200'">●</span>
              </template>
              <!-- color chip (flags) -->
              <template v-else-if="col.chip === 'color'">
                <span
                  class="inline-flex items-center gap-1.5 text-xs font-medium px-2 py-0.5 rounded-full border"
                  :class="chipClass(row.color)"
                >
                  <span class="w-1.5 h-1.5 rounded-full" :class="dotClass(row.color)"></span>{{ cellText(row, col) || '—' }}
                </span>
              </template>
              <!-- audit verdict (col.verdict = stage key on row._audit.stages) -->
              <template v-else-if="col.verdict">
                <span v-if="stageFor(row, col)" :title="stageTitle(row, col)">
                  <span :class="VERDICT_TEXT[stageFor(row, col).verdict] || 'text-gray-300'">●</span><span
                    v-if="stageFor(row, col).metric_changed"
                    class="text-amber-500 font-semibold ml-0.5"
                  >≠</span>
                </span>
                <span v-else class="text-gray-200" title="Never checked">—</span>
              </template>
              <!-- methodology / expectation sync (col.contract names which) -->
              <template v-else-if="col.contract">
                <span v-if="contractState(row, col) === 'sync'" class="text-emerald-600" :title="`${col.label} exists and matches the current SQL`">✓</span>
                <span v-else-if="contractState(row, col) === 'stale'" class="inline-flex px-1.5 py-0.5 rounded text-[10px] border bg-amber-50 text-amber-700 border-amber-200" :title="`${col.label} exists but the metric's SQL has changed since it was written`">stale</span>
                <span v-else class="text-gray-200" :title="`No ${col.label.toLowerCase()}`">—</span>
              </template>
              <!-- badge -->
              <template v-else-if="col.badge && cellValue(row, col)">
                <span
                  class="inline-flex items-center px-1.5 py-0.5 rounded text-xs border leading-tight font-mono"
                  :class="badgeClass(col.badge)"
                >{{ cellText(row, col) }}</span>
              </template>
              <template v-else-if="col.badge">
                <span class="text-gray-300">—</span>
              </template>
              <!-- count: a reference tally. Non-zero reads solid, zero recedes,
                   so "what is wired up" is legible down the column. -->
              <template v-else-if="col.count">
                <span :class="cellValue(row, col) ? 'font-semibold text-gray-900' : 'text-gray-300'">
                  {{ cellValue(row, col) ?? '—' }}
                </span>
              </template>

              <!-- plain -->
              <template v-else>{{ cellText(row, col) }}</template>
            </td>
            <td class="px-2 py-1.5 text-right">
              <button
                v-if="artifact.deleteUrl"
                class="text-gray-300 hover:text-red-500 text-sm leading-none"
                :title="`Delete ${artifact.singular.toLowerCase()}`"
                @click.stop="$emit('delete', row)"
              >×</button>
            </td>
          </tr>
          <tr v-if="!filtered.length">
            <td :colspan="artifact.columns.length + 1" class="px-3 py-8 text-center text-xs text-gray-400">
              {{ rows.length ? 'No matches.' : `No ${artifact.label.toLowerCase()} yet.` }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

const CHIP = {
  gray: 'bg-gray-50 border-gray-200 text-gray-700', red: 'bg-red-50 border-red-200 text-red-700',
  amber: 'bg-amber-50 border-amber-200 text-amber-700', sky: 'bg-sky-50 border-sky-200 text-sky-700',
  emerald: 'bg-emerald-50 border-emerald-200 text-emerald-700', violet: 'bg-violet-50 border-violet-200 text-violet-700',
};
const DOT = {
  gray: 'bg-gray-400', red: 'bg-red-500', amber: 'bg-amber-500',
  sky: 'bg-sky-500', emerald: 'bg-emerald-500', violet: 'bg-violet-500',
};
const BADGE = {
  amber: 'bg-amber-50 text-amber-700 border-amber-200',
  purple: 'bg-purple-50 text-purple-700 border-purple-200',
  gray: 'bg-gray-50 text-gray-700 border-gray-200',
};
const VERDICT_TEXT = { GREEN: 'text-emerald-500', AMBER: 'text-amber-500', RED: 'text-red-500' };
const VERDICT_RANK = { RED: 3, AMBER: 2, GREEN: 1 };
const CONTRACT_RANK = { sync: 2, stale: 1, none: 0 };

export default {
  name: 'ArtifactList',
  components: { FontAwesomeIcon },
  props: {
    artifact: { type: Object, required: true },
    rows: { type: Array, default: () => [] },
    loading: { type: Boolean, default: false },
    error: { type: String, default: null },
    creating: { type: Boolean, default: false },
  },
  emits: ['open', 'create', 'delete'],
  data() {
    return { query: '', sortKey: 'id', sortDir: 'asc', VERDICT_TEXT };
  },
  computed: {
    filtered() {
      const q = this.query.trim().toLowerCase();
      let list = this.rows;
      if (q) {
        const fields = this.artifact.searchFields || ['name'];
        list = list.filter((r) => fields.some((f) => String(r[f] ?? '').toLowerCase().includes(q)));
      }
      const col = (this.artifact.columns || []).find((c) => c.key === this.sortKey);
      const dir = this.sortDir === 'asc' ? 1 : -1;
      return list.slice().sort((a, b) => {
        const va = this.rawSortVal(a, col);
        const vb = this.rawSortVal(b, col);
        const ae = va === null || va === undefined || va === '';
        const be = vb === null || vb === undefined || vb === '';
        if (ae && be) return 0;
        if (ae) return 1;
        if (be) return -1;
        if (va < vb) return -1 * dir;
        if (va > vb) return 1 * dir;
        return 0;
      });
    },
  },
  methods: {
    // Optional per-artifact dimming: artifact.rowMuted(row) => true renders the
    // row greyed. Used by the DataAttribute catalog to fade attributes nothing
    // appears to reference. Absent on every other artifact, so no visual change.
    isMuted(row) {
      return typeof this.artifact.rowMuted === 'function' && this.artifact.rowMuted(row);
    },
    cellValue(row, col) {
      if (col.compute) return col.compute(row);
      return row[col.key];
    },
    cellText(row, col) {
      const v = this.cellValue(row, col);
      if (v === null || v === undefined || v === '') return col.check || col.dot ? '' : '—';
      if (col.date) return this.formatDate(v);
      return v;
    },
    stageFor(row, col) {
      return row._audit?.stages?.[col.verdict] || null;
    },
    stageTitle(row, col) {
      const s = this.stageFor(row, col);
      if (!s) return '';
      return `${s.verdict} (${String(s.created_at).slice(0, 10)})`
        + (s.metric_changed ? ' — metric changed since this check' : '');
    },
    contractState(row, col) {
      // col.contract = 'methodology' | 'expectation' — separate artifacts,
      // separate columns; they share the generation fingerprint.
      const c = row._audit?.contract;
      if (!c) return 'none';
      const exists = col.contract === 'methodology' ? c.has_methodology : c.has_expectation;
      if (!exists) return 'none';
      return c.fingerprint_current ? 'sync' : 'stale';
    },
    rawSortVal(row, col) {
      if (!col) return row.id;
      if (col.verdict) {
        const s = this.stageFor(row, col);
        return s ? VERDICT_RANK[s.verdict] || 0 : -1;
      }
      if (col.contract) return CONTRACT_RANK[this.contractState(row, col)];
      const v = this.cellValue(row, col);
      if (typeof v === 'string') return v.toLowerCase();
      if (typeof v === 'boolean') return v ? 1 : 0;
      return v;
    },
    toggleSort(key) {
      if (this.sortKey === key) this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc';
      else { this.sortKey = key; this.sortDir = 'asc'; }
    },
    formatDate(iso) {
      const d = new Date(iso);
      if (isNaN(d)) return '—';
      return `${d.getMonth() + 1}/${d.getDate()}/${String(d.getFullYear()).slice(2)}`;
    },
    chipClass(c) { return CHIP[c] || CHIP.gray; },
    dotClass(c) { return DOT[c] || DOT.gray; },
    badgeClass(b) { return BADGE[b] || BADGE.gray; },
  },
};
</script>
